import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  type DocumentReference,
  type DocumentSnapshot
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import type { OrgMembership, OrgRole } from '@finvox/shared/org';
import {
  type CashbookAccess
} from '@finvox/shared/cashbook-access';
import {
  type MemberAccess,
  memberPermissionsFromMemberDoc,
  resolveMemberAccess
} from '@finvox/shared/member-access';
import { safeLocalStorageGet, safeLocalStorageSet, lastOrgIdStorageKey } from '@finvox/shared/storage';
import { getFirebaseAuth, getFirestoreDb } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { FriendlyErrorCode } from '@/lib/friendlyError';
import { getFriendlyErrorCode } from '@/lib/friendlyError';
import { isOrgLoadStale } from '@/lib/orgLoadGuard';
import { shouldSkipOrgLiveUpdate } from '@/lib/orgSnapshotGuard';
import { parseOrgBranding } from '@/lib/orgBranding';
import { pickAccessibleOrgId } from '@/lib/orgResolve';
import {
  discoverMembershipsForUid,
  userProfilePatchFromMemberships
} from '@/lib/syncUserMemberships';
import { FIRESTORE_OP_TIMEOUT_MS, withTimeout } from '@/lib/withTimeout';

export type FiscalPeriod = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed';
};

export type Organization = {
  id: string;
  name: string;
  countryTemplate: string;
  role: OrgRole;
};

function emptyOrgSessionState() {
  return {
    org: null as Organization | null,
    memberships: [] as OrgMembership[],
    periods: [] as FiscalPeriod[],
    brandingDisplayName: null as string | null,
    brandingLogoUrl: null as string | null,
    memberAccess: null as MemberAccess | null,
    cashbookAccess: null as CashbookAccess | null,
    needsOnboarding: false,
    orgLoadError: null as FriendlyErrorCode | null
  };
}
type OrgContextValue = {
  org: Organization | null;
  orgId: string | null;
  memberships: OrgMembership[];
  periods: FiscalPeriod[];
  currentPeriodId: string | null;
  setCurrentPeriodId: (id: string) => void;
  switchOrg: (orgId: string) => Promise<void>;
  loading: boolean;
  needsOnboarding: boolean;
  orgLoadError: FriendlyErrorCode | null;
  refreshOrg: (options?: { silent?: boolean }) => Promise<void>;
  brandingDisplayName: string | null;
  brandingLogoUrl: string | null;
  brandName: string;
  memberAccess: MemberAccess | null;
  cashbookAccess: CashbookAccess | null;
  /** Ignore cached / wrong-account live updates (use in onSnapshot handlers). */
  skipLiveUpdate: (metadata: import('firebase/firestore').SnapshotMetadata, forOrgId: string) => boolean;
};

const OrgContext = createContext<OrgContextValue | null>(null);

type FirestoreDb = ReturnType<typeof getFirestoreDb>;

async function readDocFresh(ref: DocumentReference): Promise<DocumentSnapshot> {
  try {
    return await getDocFromServer(ref);
  } catch {
    return await getDoc(ref);
  }
}

async function membershipFromOrgId(
  db: FirestoreDb,
  uid: string,
  orgId: string
): Promise<OrgMembership | null> {
  const memberSnap = await readDocFresh(doc(db, 'organizations', orgId, 'members', uid));
  if (!memberSnap.exists()) return null;
  const orgSnap = await readDocFresh(doc(db, 'organizations', orgId));
  return {
    orgId,
    orgName: String(orgSnap.data()?.name ?? orgId),
    role: (memberSnap.data().role as OrgRole) ?? 'owner'
  };
}

/** Maps loadOrg failures — never treat errors as missing org. */
export function orgLoadFailureState(error: unknown): {
  needsOnboarding: false;
  orgLoadError: FriendlyErrorCode;
} {
  return { needsOnboarding: false, orgLoadError: getFriendlyErrorCode(error) };
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const loadGenerationRef = useRef(0);
  const sessionEpochRef = useRef(0);
  const loadInFlightRef = useRef<string | null>(null);
  const activeOrgIdRef = useRef<string | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [periods, setPeriods] = useState<FiscalPeriod[]>([]);
  const [currentPeriodId, setCurrentPeriodIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [orgLoadError, setOrgLoadError] = useState<FriendlyErrorCode | null>(null);
  const [brandingDisplayName, setBrandingDisplayName] = useState<string | null>(null);
  const [brandingLogoUrl, setBrandingLogoUrl] = useState<string | null>(null);
  const [memberAccess, setMemberAccess] = useState<MemberAccess | null>(null);
  const [cashbookAccess, setCashbookAccess] = useState<CashbookAccess | null>(null);

  const setCurrentPeriodId = useCallback(
    (id: string) => {
      setCurrentPeriodIdState(id);
      if (user?.uid && org?.id) {
        safeLocalStorageSet(lastOrgIdStorageKey(user.uid), org.id);
      }
    },
    [org?.id, user?.uid]
  );

  useEffect(() => {
    loadGenerationRef.current += 1;
    sessionEpochRef.current += 1;
    loadInFlightRef.current = null;
    activeOrgIdRef.current = null;
    const cleared = emptyOrgSessionState();
    setOrg(cleared.org);
    setMemberships(cleared.memberships);
    setPeriods(cleared.periods);
    setBrandingDisplayName(cleared.brandingDisplayName);
    setBrandingLogoUrl(cleared.brandingLogoUrl);
    setMemberAccess(cleared.memberAccess);
    setCashbookAccess(cleared.cashbookAccess);
    setNeedsOnboarding(cleared.needsOnboarding);
    setOrgLoadError(cleared.orgLoadError);
    setCurrentPeriodIdState(null);
    if (user?.uid) setLoading(true);
  }, [user?.uid]);

  const loadOrgData = useCallback(
    async (orgId: string, role: OrgRole, generation: number, epoch: number, uid: string) => {
      const stale = () =>
        isOrgLoadStale(
          uid,
          generation,
          loadGenerationRef.current,
          getFirebaseAuth().currentUser?.uid,
          epoch,
          sessionEpochRef.current
        );

      const db = getFirestoreDb();
      setBrandingDisplayName(null);
      setBrandingLogoUrl(null);
      setMemberAccess(null);
      setCashbookAccess(null);
      activeOrgIdRef.current = null;

      const orgSnap = await readDocFresh(doc(db, 'organizations', orgId));
      if (stale()) return;

      const memberSnap = await readDocFresh(doc(db, 'organizations', orgId, 'members', uid));
      if (stale()) return;

      const brandingSnap = await readDocFresh(
        doc(db, 'organizations', orgId, 'settings', 'branding')
      );
      if (stale()) return;

      const data = orgSnap.data();
      const branding = parseOrgBranding(brandingSnap.data());
      activeOrgIdRef.current = orgId;
      setOrg({
        id: orgId,
        name: String(data?.name ?? 'Organization'),
        countryTemplate: String(data?.countryTemplate ?? 'generic'),
        role
      });
      setBrandingDisplayName(branding.displayName);
      setBrandingLogoUrl(branding.logoUrl);
      const { overrides, legacyCashbook } = memberPermissionsFromMemberDoc(memberSnap.data());
      const access = resolveMemberAccess(role, overrides, legacyCashbook);
      setMemberAccess(access);
      setCashbookAccess(access.cashbook);
      setNeedsOnboarding(false);
    },
    []
  );

  const loadOrg = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      const setBusy = (value: boolean) => {
        if (!silent) setLoading(value);
      };

      const uid = user?.uid;
      if (!uid) {
        const cleared = emptyOrgSessionState();
        setOrg(cleared.org);
        setMemberships(cleared.memberships);
        setPeriods(cleared.periods);
        setBrandingDisplayName(cleared.brandingDisplayName);
        setBrandingLogoUrl(cleared.brandingLogoUrl);
        setMemberAccess(cleared.memberAccess);
        setCashbookAccess(cleared.cashbookAccess);
        setOrgLoadError(cleared.orgLoadError);
        setBusy(false);
        return;
      }

      if (loadInFlightRef.current === uid) return;

      const generation = ++loadGenerationRef.current;
      const epoch = sessionEpochRef.current;
      const stale = () =>
        isOrgLoadStale(
          uid,
          generation,
          loadGenerationRef.current,
          getFirebaseAuth().currentUser?.uid,
          epoch,
          sessionEpochRef.current
        );

      loadInFlightRef.current = uid;
      setOrgLoadError(null);
      setBusy(true);
      const db = getFirestoreDb();

      try {
        await withTimeout(
          (async () => {
            if (stale()) return;

            const userSnap = await readDocFresh(doc(db, 'users', uid));
            if (stale()) return;

            let list = (userSnap.data()?.memberships ?? []) as OrgMembership[];

            if (!list.length) {
              const defaultOrgId = userSnap.data()?.defaultOrgId as string | undefined;
              if (defaultOrgId) {
                const entry = await membershipFromOrgId(db, uid, defaultOrgId);
                if (stale()) return;
                if (entry) {
                  list = [entry];
                  await setDoc(doc(db, 'users', uid), { memberships: list }, { merge: true });
                }
              }
            }

            if (!list.length) {
              try {
                list = await discoverMembershipsForUid(db, uid);
                if (stale()) return;
                if (list.length) {
                  await setDoc(
                    doc(db, 'users', uid),
                    userProfilePatchFromMemberships(list),
                    { merge: true }
                  );
                }
              } catch (e) {
                console.warn('[OrgContext] membership sync failed', e);
              }
            }

            if (stale()) return;
            setMemberships(list);

            let ownedOrgId: string | undefined;
            try {
              const owned = await getDocs(
                query(collection(db, 'organizations'), where('ownerId', '==', uid))
              );
              if (stale()) return;
              ownedOrgId = owned.docs[0]?.id;
            } catch (e) {
              console.warn('[OrgContext] owned-org lookup failed', e);
            }

            const storedDefaultOrgId = userSnap.data()?.defaultOrgId as string | undefined;
            const orgHint = safeLocalStorageGet(lastOrgIdStorageKey(uid)) ?? undefined;
            const orgId = await pickAccessibleOrgId(
              [storedDefaultOrgId, list[0]?.orgId, ownedOrgId, orgHint],
              list,
              async (candidateOrgId) => (await membershipFromOrgId(db, uid, candidateOrgId)) !== null
            );

            if (stale()) return;

            if (!orgId) {
              setNeedsOnboarding(true);
              setOrg(null);
              activeOrgIdRef.current = null;
              setPeriods([]);
              setBrandingDisplayName(null);
              setBrandingLogoUrl(null);
              return;
            }

            if (storedDefaultOrgId && storedDefaultOrgId !== orgId) {
              await setDoc(doc(db, 'users', uid), { defaultOrgId: orgId }, { merge: true });
            }

            if (!list.some((m) => m.orgId === orgId)) {
              const entry = await membershipFromOrgId(db, uid, orgId);
              if (stale()) return;
              if (entry) {
                list = [entry, ...list.filter((m) => m.orgId !== orgId)];
                setMemberships(list);
                await setDoc(doc(db, 'users', uid), { memberships: list, defaultOrgId: orgId }, { merge: true });
              }
            }

            safeLocalStorageSet(lastOrgIdStorageKey(uid), orgId);

            const role = list.find((m) => m.orgId === orgId)?.role ?? 'owner';
            await loadOrgData(orgId, role, generation, epoch, uid);
          })(),
          FIRESTORE_OP_TIMEOUT_MS,
          'loadOrg'
        );
        if (!stale()) setBusy(false);
      } catch (e) {
        if (stale()) return;
        console.warn('[OrgContext] loadOrg failed', e);
        const failure = orgLoadFailureState(e);
        setNeedsOnboarding(failure.needsOnboarding);
        setOrgLoadError(failure.orgLoadError);
        setOrg(null);
        activeOrgIdRef.current = null;
        setPeriods([]);
        setBrandingDisplayName(null);
        setBrandingLogoUrl(null);
        setBusy(false);
      } finally {
        if (loadInFlightRef.current === uid) loadInFlightRef.current = null;
      }
    },
    [user?.uid, loadOrgData]
  );

  const switchOrg = useCallback(
    async (orgId: string) => {
      if (!user?.uid) return;
      const generation = ++loadGenerationRef.current;
      const epoch = sessionEpochRef.current;
      const role = memberships.find((m) => m.orgId === orgId)?.role ?? 'viewer';
      setBrandingDisplayName(null);
      setBrandingLogoUrl(null);
      setMemberAccess(null);
      setCashbookAccess(null);
      activeOrgIdRef.current = null;
      await setDoc(doc(getFirestoreDb(), 'users', user.uid), { defaultOrgId: orgId }, { merge: true });
      safeLocalStorageSet(lastOrgIdStorageKey(user.uid), orgId);
      await loadOrgData(orgId, role, generation, epoch, user.uid);
    },
    [user?.uid, memberships, loadOrgData]
  );

  useEffect(() => {
    void loadOrg();
  }, [loadOrg]);

  useEffect(() => {
    if (!org?.id || !user?.uid) return;
    const orgId = org.id;
    const uid = user.uid;
    const epoch = sessionEpochRef.current;
    const db = getFirestoreDb();
    const unsub = onSnapshot(collection(db, 'organizations', orgId, 'fiscalPeriods'), (snap) => {
      if (shouldSkipOrgLiveUpdate(snap.metadata, uid, orgId, activeOrgIdRef.current)) return;
      if (sessionEpochRef.current !== epoch) return;
      const list = snap.docs.map((d) => {
        const x = d.data();
        return {
          id: d.id,
          name: String(x.name ?? d.id),
          startDate: String(x.startDate ?? ''),
          endDate: String(x.endDate ?? ''),
          status: (x.status === 'closed' ? 'closed' : 'open') as 'open' | 'closed'
        };
      });
      list.sort((a, b) => a.startDate.localeCompare(b.startDate));
      setPeriods(list);
      setCurrentPeriodIdState((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        const open = list.find((p) => p.status === 'open');
        return open?.id ?? list[0]?.id ?? null;
      });
    });
    return unsub;
  }, [org?.id, user?.uid]);

  useEffect(() => {
    if (!org?.id || !user?.uid) return;
    const orgId = org.id;
    const uid = user.uid;
    const epoch = sessionEpochRef.current;
    const orgRef = doc(getFirestoreDb(), 'organizations', orgId);
    return onSnapshot(orgRef, (snap) => {
      if (shouldSkipOrgLiveUpdate(snap.metadata, uid, orgId, activeOrgIdRef.current)) return;
      if (sessionEpochRef.current !== epoch) return;
      const data = snap.data();
      if (!data) return;
      const name = String(data.name ?? 'Organization');
      setOrg((prev) =>
        prev?.id === orgId
          ? { ...prev, name, countryTemplate: String(data.countryTemplate ?? prev.countryTemplate) }
          : prev
      );
      setMemberships((prev) => prev.map((m) => (m.orgId === orgId ? { ...m, orgName: name } : m)));
    });
  }, [org?.id, user?.uid]);

  useEffect(() => {
    if (!org?.id || !user?.uid) {
      setBrandingDisplayName(null);
      setBrandingLogoUrl(null);
      return;
    }
    const orgId = org.id;
    const uid = user.uid;
    const epoch = sessionEpochRef.current;
    const unsub = onSnapshot(
      doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'branding'),
      (snap) => {
        if (shouldSkipOrgLiveUpdate(snap.metadata, uid, orgId, activeOrgIdRef.current)) return;
        if (orgId !== activeOrgIdRef.current) return;
        if (sessionEpochRef.current !== epoch) return;
        const branding = parseOrgBranding(snap.data());
        setBrandingDisplayName(branding.displayName);
        setBrandingLogoUrl(branding.logoUrl);
      }
    );
    return unsub;
  }, [org?.id, user?.uid]);

  const brandName = brandingDisplayName?.trim() || org?.name || 'FinVox';

  const skipLiveUpdate = useCallback(
    (metadata: import('firebase/firestore').SnapshotMetadata, forOrgId: string) => {
      const uid = user?.uid;
      if (!uid) return true;
      return shouldSkipOrgLiveUpdate(metadata, uid, forOrgId, activeOrgIdRef.current);
    },
    [user?.uid]
  );

  const value = useMemo(
    () => ({
      org,
      orgId: org?.id ?? null,
      memberships,
      periods,
      currentPeriodId,
      setCurrentPeriodId,
      switchOrg,
      loading,
      needsOnboarding,
      orgLoadError,
      refreshOrg: loadOrg,
      brandingDisplayName,
      brandingLogoUrl,
      brandName,
      memberAccess,
      cashbookAccess,
      skipLiveUpdate
    }),
    [
      org,
      memberships,
      periods,
      currentPeriodId,
      switchOrg,
      loading,
      needsOnboarding,
      orgLoadError,
      loadOrg,
      brandingDisplayName,
      brandingLogoUrl,
      brandName,
      memberAccess,
      cashbookAccess,
      skipLiveUpdate
    ]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg within OrgProvider');
  return ctx;
}
